import { useState } from 'react';
import './bars.css';

/**
 * Plain bars, scaled to the largest value.
 *
 * Zero-height bars are given a visible sliver so a quiet day reads as a quiet
 * day rather than as missing data — the two look identical at 0px and mean
 * quite different things. The busiest bar is drawn in the full accent, the
 * rest in its lighter step, so the peak reads before anything is tapped.
 *
 * Tapping a bar names it above the chart. That is the whole of the interaction:
 * a chart on a tablet cannot rely on hover, and a number a manager cannot read
 * is not in the report at all.
 *
 * `value` only sizes the bar; what is read is `caption`, which the caller
 * formats (money with `formatMoney`, in the currency it is in).
 *
 * `fit` is for a desk: every bar shares the width instead of keeping a
 * fingertip's, so a month or a quarter is seen whole rather than scrolled, and
 * only every few bars is labelled — the caption above names the busiest and
 * the picked one.
 */
export function Bars({
  bars,
  unit,
  fit = false,
}: {
  bars: { key: string; label: string; value: number; caption: string }[];
  /** What one bar is, in a word: "day", "hour". */
  unit: string;
  fit?: boolean | undefined;
}) {
  const [pickedKey, setPickedKey] = useState<string | null>(null);
  const peak = Math.max(...bars.map((bar) => bar.value), 0);
  const picked = bars.find((bar) => bar.key === pickedKey) ?? null;
  const busiest = bars.reduce<(typeof bars)[number] | null>(
    (best, bar) => (best === null || bar.value > best.value ? bar : best),
    null,
  );
  // About seven labels across, whatever the count: enough to place a bar,
  // few enough to fit a phone.
  const labelEvery = fit ? Math.max(1, Math.ceil(bars.length / 7)) : 1;

  return (
    <>
      <p className="ui-bars__caption">
        <span className="ui-bars__picked">
          {picked ? `${picked.label} · ${picked.caption}` : `Tap a ${unit} for its total`}
        </span>
        {busiest && (
          <span className="ui-bars__peak">
            Busiest {unit}: {busiest.label} · {busiest.caption}
          </span>
        )}
      </p>

      <div className={`ui-bars${fit ? ' ui-bars--fit' : ''}`}>
        {bars.map((bar, index) => (
          <button
            key={bar.key}
            type="button"
            className={
              `ui-bars__slot${bar.key === pickedKey ? ' is-on' : ''}` +
              (busiest !== null && bar.key === busiest.key && peak > 0 ? ' is-peak' : '')
            }
            aria-pressed={bar.key === pickedKey}
            aria-label={`${bar.label}, ${bar.caption}`}
            onClick={() => setPickedKey(bar.key === pickedKey ? null : bar.key)}
          >
            <span
              className="ui-bars__bar"
              style={{ height: peak > 0 ? `${Math.max((bar.value / peak) * 100, 2)}%` : '2%' }}
            />
            <span
              className={`ui-bars__label${
                (bars.length - 1 - index) % labelEvery === 0 ? '' : ' is-quiet'
              }`}
            >
              {bar.label}
            </span>
          </button>
        ))}
      </div>
    </>
  );
}
